import React, { useEffect, useImperativeHandle, useRef, forwardRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import {
  makeSidingTexture,
  makeMetalRoofTexture,
  makeGrassTexture,
  makeStairsTexture,
  makeBrickTexture,
  makeStoneTexture,
  makeWoodPlankTexture,
  makeConcreteTexture,
  buildRoofMaterial,
  buildFloor,
  buildRoof,
  buildDormer,
  buildSkylight,
  buildSolarPanel,
  buildStaircase,
  buildChimney,
  buildDeck,
  buildWalkway,
  buildTree,
} from "../three/BIMPrimitives";
import { buildSymbolsForFloor } from "../three/symbols3d";
import { runAutoFit as runAutoFitHelper } from "../three/autoFit";
import useClimate from "../three/hooks/useClimate";
import useCompassYaw from "../three/hooks/useCompassYaw";
import useSectionCut from "../three/hooks/useSectionCut";
import useTapToTeleport from "../three/hooks/useTapToTeleport";
import useCinematicTour from "../three/hooks/useCinematicTour";
import {
  tmpV3 as _tmpV3,
  disposeNodeStatic,
  applyInteriorWallVisibility,
  applyInteriorOcclusion,
  restoreInteriorOpacity,
  applyDraftingMode,
  applyGhostWall,
  applyDollhouseFade,
  commitSeamlessNavOpacity,
} from "../three/effects";
import MiniMap from "./MiniMap";

// ============================================================================
// ThreeViewport — React wrapper around a Three.js scene driven by a BIM
// blueprint. The component owns: renderer/camera/lighting setup, the per-
// blueprint geometry rebuild, and the master tick loop. Specialised feature
// logic lives in `/three/hooks/`:
//   • useClimate         — fog/bg/hemi/sun-multiplier from build_site_metadata
//   • useCompassYaw      — camera-yaw → compass needle degrees
//   • useSectionCut      — per-material clipping plane for X-Ray cutaways
//   • useTapToTeleport   — pointer raycast + teleport ring + glide animation
//   • useCinematicTour   — sequential teleports through tour waypoints
// V1.8.9 — Visual-effect helpers (X-Ray walls, Drafting Mode, Ghost Wall,
// Dollhouse Fade) extracted into `/three/effects.js`. The component now
// imports them rather than redefining them at module scope.
// ============================================================================


const ThreeViewport = forwardRef(function ThreeViewport(
  { blueprint, isLoading, style, visibleFloors, onFloorsAvailable, timeOfDay = 12, showInteriorWalls = true, xrayInteriorWalls = false, draftingMode = false, onRenderError, onMiniMapTeleport },
  ref,
) {
  // --- Scene refs ----------------------------------------------------------
  // Stable refs for callback props so they don't end up in useEffect deps
  // and trigger constant rebuild loops every time the parent re-renders.
  const onRenderErrorRef = useRef(onRenderError);
  onRenderErrorRef.current = onRenderError;
  const onFloorsAvailableRef = useRef(onFloorsAvailable);
  onFloorsAvailableRef.current = onFloorsAvailable;
  // Smart-occlusion toggle is read inside the master tick loop; storing in a
  // ref keeps the loop closure stable across re-renders.
  const xrayInteriorWallsRef = useRef(xrayInteriorWalls);
  xrayInteriorWallsRef.current = xrayInteriorWalls;
  // V1.6 — Drafting Mode (Whiteprint aesthetic). Read inside the master tick
  // loop + imperative methods. We also keep an ambient-light ref since
  // drafting mode bumps it from 0 → full so MeshBasicMaterial doesn't go pitch
  // black (it doesn't strictly need the light, but it costs ~0 perf).
  const draftingModeRef = useRef(draftingMode);
  draftingModeRef.current = draftingMode;
  const ambientRef = useRef(null);
  // Cached scene-state container for the drafting toggle (one place to stash
  // original bg / fog / sun.intensity so revert is clean).
  const draftingStateRef = useRef({});
  // V1.8.1 — auto-fit-to-front sentinel. The camera initial position is a
  // global fallback; on the first blueprint build we re-fit the camera so it
  // sits in front of the ACTUAL house bbox (centered architect-mode designs
  // can otherwise put the camera behind the structure).
  const firstFitDoneRef = useRef(false);
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const houseGroupRef = useRef(null);
  const envGroupRef = useRef(null);
  const animFrameRef = useRef(null);
  const sunRef = useRef(null);
  const hemiRef = useRef(null);
  const groundRef = useRef(null);
  const originalGroundRef = useRef(null);
  const floorGroupsRef = useRef({});
  const walkthroughFloorYRef = useRef(1.68);

  // Procedural texture refs (built once, disposed on unmount).
  const sidingTexRef = useRef(null);
  const metalRoofTexRef = useRef(null);
  const grassTexRef = useRef(null);
  const stairsTexRef = useRef(null);
  const brickTexRef = useRef(null);
  const stoneTexRef = useRef(null);
  const woodPlankTexRef = useRef(null);
  const concreteTexRef = useRef(null);

  // --- Hook wiring ---------------------------------------------------------
  const { climateSunMultRef } = useClimate(blueprint, timeOfDay, {
    sceneRef, sunRef, hemiRef,
  });
  const { compassDeg, cameraPose, updateFromCamera } = useCompassYaw();
  const { applyClipping } = useSectionCut();
  const teleport = useTapToTeleport({ walkthroughFloorYRef });
  const tour = useCinematicTour({
    beginTeleport: teleport.beginTeleport,
    teleportRef: teleport.teleportRef,
  });

  // Wire the tour to advance whenever a teleport completes.
  useEffect(() => {
    tour.attachTeleportComplete(teleport.setOnTeleportComplete);
  }, [tour, teleport.setOnTeleportComplete]);

  // --- Imperative handle (parent calls these via ref) ----------------------
  // V1.8.2 — extracted auto-fit logic so both the first-build effect AND the
  // imperative `resetView()` method (wired to ViewOptionsPanel) share one
  // implementation. Returns true if the fit ran, false if the scene wasn't
  // ready (e.g. blueprint hadn't built yet).
  const runAutoFit = () => runAutoFitHelper({
    THREE,
    camera: cameraRef.current,
    controls: controlsRef.current,
    group: houseGroupRef.current,
  });

  useImperativeHandle(ref, () => ({
    getSnapshotDataUrl: ({ forceAllFloors = false } = {}) => {
      try {
        if (!rendererRef.current || !sceneRef.current || !cameraRef.current) return null;
        let restored = [];
        if (forceAllFloors && houseGroupRef.current) {
          houseGroupRef.current.traverse((node) => {
            if (!node.isMesh && !node.isLineSegments) return;
            const mats = Array.isArray(node.material) ? node.material : [node.material];
            mats.forEach((m) => {
              if (m && m.clippingPlanes && m.clippingPlanes.length > 0) {
                restored.push([m, m.clippingPlanes]);
                m.clippingPlanes = [];
              }
            });
          });
        }
        if (teleport.indicatorRef.current) teleport.indicatorRef.current.visible = false;
        rendererRef.current.render(sceneRef.current, cameraRef.current);
        const url = rendererRef.current.domElement.toDataURL("image/png");
        restored.forEach(([m, planes]) => { m.clippingPlanes = planes; });
        // V2.0.86 — an empty/uninitialised WebGL context returns
        // "data:," which callers cannot use. Coerce to null so parents
        // (e.g. share-transform) fall back to their placeholder path
        // instead of POSTing a garbage payload the backend rejects.
        if (!url || !url.startsWith("data:image/")) return null;
        return url;
      } catch (e) { return null; }
    },
    captureFrame: () => {
      try {
        if (!rendererRef.current) return null;
        const url = rendererRef.current.domElement.toDataURL("image/png");
        if (!url || !url.startsWith("data:image/")) return null;
        return url;
      } catch (e) { return null; }
    },
    /**
     * Hero-shot snapshot at 2048px wide for a clean PR-ready render.
     *  • Hides the teleport indicator + clears all section-cut clipping.
     *  • Frames the camera on the house bounding box at a 35° elevation.
     *  • Temporarily resizes the WebGL drawing buffer to 2048×1280 and
     *    restores the live size after capture.
     * Returns a base64 PNG dataURL (or null if the renderer isn't ready).
     */
    captureProjectSnapshot: ({ width = 2048, height = 1280 } = {}) => {
      try {
        const renderer = rendererRef.current;
        const scene = sceneRef.current;
        const camera = cameraRef.current;
        if (!renderer || !scene || !camera) return null;

        // 1. Save current state.
        const liveW = renderer.domElement.width;
        const liveH = renderer.domElement.height;
        const livePixelRatio = renderer.getPixelRatio();
        const liveCamPos = camera.position.clone();
        const liveTarget = controlsRef.current?.target.clone();
        const liveAspect = camera.aspect;
        const liveFov = camera.fov;

        // 2. Clear clipping + hide indicator for the hero render.
        const restored = [];
        if (houseGroupRef.current) {
          houseGroupRef.current.traverse((node) => {
            if (!node.isMesh && !node.isLineSegments) return;
            const mats = Array.isArray(node.material) ? node.material : [node.material];
            mats.forEach((m) => {
              if (m && m.clippingPlanes && m.clippingPlanes.length > 0) {
                restored.push([m, m.clippingPlanes]);
                m.clippingPlanes = [];
              }
            });
          });
        }
        if (teleport.indicatorRef.current) teleport.indicatorRef.current.visible = false;

        // 3. Hero-shot camera: 35° elevation, 36° azimuth around bounding box.
        //    Camera sits on the FRONT side of the house (negative z) so the
        //    captured hero matches the 2D Plan tab and shows the front facade.
        const box = new THREE.Box3().setFromObject(houseGroupRef.current || scene);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        const radius = Math.max(size.x, size.z) * 0.5;
        const elevation = Math.max(size.y * 1.4, radius * 0.7, 8);
        const distance = Math.max(radius * 2.2, 18);
        const azimuth = Math.PI / 5; // 36° around Y
        camera.position.set(
          center.x + Math.sin(azimuth) * distance,
          center.y + elevation,
          center.z - Math.cos(azimuth) * distance,
        );
        camera.lookAt(center.x, center.y + 0.5, center.z);
        camera.fov = 38;

        // 4. Resize buffer to 2048×1280 (off-screen), render, capture.
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        renderer.setPixelRatio(1);
        renderer.setSize(width, height, false);
        renderer.render(scene, camera);
        const url = renderer.domElement.toDataURL("image/png");

        // 5. Restore everything.
        renderer.setPixelRatio(livePixelRatio);
        renderer.setSize(liveW / livePixelRatio, liveH / livePixelRatio, false);
        camera.position.copy(liveCamPos);
        if (liveTarget && controlsRef.current) controlsRef.current.target.copy(liveTarget);
        camera.aspect = liveAspect;
        camera.fov = liveFov;
        camera.updateProjectionMatrix();
        restored.forEach(([m, planes]) => { m.clippingPlanes = planes; });
        renderer.render(scene, camera);
        return url;
      } catch (e) { return null; }
    },
    playTour: (waypoints, onDone) => tour.playTour(waypoints, onDone),
    stopTour: () => tour.stopTour(),
    /**
     * V1.6 — Top-Down 2D camera snap. Frames the camera straight down over
     * the house centroid so the scene reads as a true blueprint plan view.
     * Pairs with Drafting Mode (parent auto-engages it via the same button)
     * for a signature-ready 1:N CAD shot.
     */
    snapTopDown: () => {
      try {
        const camera = cameraRef.current;
        const controls = controlsRef.current;
        const scene = sceneRef.current;
        if (!camera || !controls || !scene) return;
        const target = houseGroupRef.current || scene;
        const box = new THREE.Box3().setFromObject(target);
        if (!isFinite(box.min.x) || !isFinite(box.max.x)) return;
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        const radius = Math.max(size.x, size.z, 6) * 0.55;
        // Drop the camera high above the centroid; small tilt for parallax.
        camera.position.set(center.x, center.y + Math.max(radius * 1.6, 22), center.z + 0.001);
        controls.target.set(center.x, center.y, center.z);
        camera.up.set(0, 0, -1); // North = -Z so floor reads top-down.
        camera.lookAt(center);
        controls.update();
      } catch (_) { /* swallow */ }
    },

    // V1.8.2 — Reset View. Re-runs the same auto-fit-to-front pass that
    // happens on the first blueprint load, so users can recover from
    // awkward orbits without reloading the page.
    resetView: () => {
      runAutoFit();
    },

    // V1.8.2 — Cinematic First-View capture. Used by Editor.jsx once per
    // design (on first blueprint load with rooms) to record a 3-second,
    // 30-frame orbit GIF that becomes the dashboard tile thumbnail.
    //
    // Process:
    //   1. Snapshot the current camera/target.
    //   2. runAutoFit() to land at the canonical front view.
    //   3. Capture `frames` evenly-spaced shots while rotating the camera
    //      `arcDeg` degrees around the bbox center (Y axis).
    //   4. Restore the original camera/target.
    //
    // Returns: Promise<string[]> of data:image/png;base64,... URLs.
    captureCinematicReel: async ({ frames = 30, arcDeg = 30 } = {}) => {
      const camera = cameraRef.current;
      const controls = controlsRef.current;
      const renderer = rendererRef.current;
      const scene = sceneRef.current;
      const group = houseGroupRef.current;
      if (!camera || !controls || !renderer || !scene || !group) return [];
      if (group.children.length === 0) return [];
      // Snapshot current camera state so we can restore at the end.
      const startCamPos = camera.position.clone();
      const startTarget = controls.target.clone();
      const startUp = camera.up.clone();
      const startAutoRotate = controls.autoRotate;
      controls.autoRotate = false;
      try {
        // Step 1: land at the auto-fit front view.
        if (!runAutoFit()) return [];
        // Step 2: compute the rotation pivot (bbox center) + radius.
        const box = new THREE.Box3().setFromObject(group);
        const center = box.getCenter(new THREE.Vector3());
        const startAngle = Math.atan2(
          camera.position.x - center.x,
          camera.position.z - center.z,
        );
        const dy = camera.position.y - center.y;
        const dxz = Math.hypot(
          camera.position.x - center.x,
          camera.position.z - center.z,
        );
        const arcRad = (arcDeg * Math.PI) / 180;
        // Center the orbit on the auto-fit angle so the loop swings ±arc/2.
        const angleStart = startAngle - arcRad / 2;
        const out = [];
        for (let i = 0; i < frames; i++) {
          const t = frames === 1 ? 0 : i / (frames - 1);
          const a = angleStart + arcRad * t;
          camera.position.set(
            center.x + Math.sin(a) * dxz,
            center.y + dy,
            center.z + Math.cos(a) * dxz,
          );
          camera.lookAt(center.x, center.y + 0.5, center.z);
          controls.target.set(center.x, center.y + 0.5, center.z);
          controls.update();
          // Force a render and grab the pixels.
          renderer.render(scene, camera);
          try {
            out.push(renderer.domElement.toDataURL("image/png"));
          } catch (_) { /* tainted canvas — skip frame */ }
          // Yield to the event loop so the renderer can flush and avoid a
          // multi-second main-thread block during capture.
          await new Promise((r) => setTimeout(r, 0));
        }
        return out;
      } finally {
        // Always restore.
        camera.position.copy(startCamPos);
        controls.target.copy(startTarget);
        camera.up.copy(startUp);
        camera.lookAt(controls.target);
        controls.autoRotate = startAutoRotate;
        controls.update();
      }
    },
  }));

  // V1.6 — Drafting Mode application. React-controlled prop drives a
  // material + lighting swap on the entire houseGroup. Runs whenever the
  // prop flips OR a new blueprint lands (the rebuild creates fresh meshes
  // that need their materials swapped to match the active mode).
  useEffect(() => {
    const houseGroup = houseGroupRef.current;
    const scene = sceneRef.current;
    if (!houseGroup || !scene) return;
    // Hydrate the persistent state object with live refs so applyDraftingMode
    // can mutate the cached origs in place across invocations.
    const state = draftingStateRef.current;
    state.renderer = rendererRef.current;
    state.sun = sunRef.current;
    state.hemi = hemiRef.current;
    state.ambient = ambientRef.current;
    state.envGroup = envGroupRef.current;
    applyDraftingMode(houseGroup, scene, state, draftingMode);
  }, [draftingMode, blueprint]);

  // ---- Scene / renderer setup (one-time) ----------------------------------
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const width = mount.clientWidth;
    const height = mount.clientHeight;

    const scene = new THREE.Scene();
    sceneRef.current = scene;
    // Test-only exposure for Playwright (read-only). Safe in prod (no behavior change).
    if (typeof window !== "undefined") { window.__threeScene = scene; }
    // V2.0.22 — Sky blue daytime palette (was a deep midnight navy).
    // Picking colours that read as "real sky" while keeping the brutalist
    // CAD typography legible against the horizon.
    scene.background = new THREE.Color(0x9ccdf2);
    scene.fog = new THREE.Fog(0xb8d8ec, 55, 140);

    // Near clip 0.05 (was 0.1) so the camera can fly INTO walls without the
    // 'stitching' / clipping artefact when passing through geometry. Combined
    // with minDistance=0.5 below, this gives free interior navigation.
    const camera = new THREE.PerspectiveCamera(50, width / height, 0.05, 500);
    // V1.7.9/V1.9.8 — Camera sits at NEGATIVE z, head-on at the FRONT facade
    // (no x-offset). The 2D Plan tab uses bottom=FRONT/top=REAR, so a head-on
    // -z camera in 3D produces the matching orientation: rooms render in the
    // same horizontal positions across both views.
    camera.position.set(0, 14, -22);
    cameraRef.current = camera;

    // V2.0.5 — Auto-downgrade rendering quality on touch / low-DPR / small
    // screens (≤ 1024 px = tablets + phones). Disables soft shadows + caps
    // pixel ratio + disables antialiasing, which roughly halves frame cost
    // on iPad-class hardware. Desktop browsers keep the full quality path.
    const lowEndGPU = typeof window !== "undefined"
      && (window.innerWidth <= 1024 || window.matchMedia?.("(pointer: coarse)").matches);

    const renderer = new THREE.WebGLRenderer({
      antialias: !lowEndGPU, alpha: false, preserveDrawingBuffer: true,
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, lowEndGPU ? 1.25 : 2));
    renderer.shadowMap.enabled = !lowEndGPU;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    renderer.localClippingEnabled = true;
    mount.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Sky shader dome — V2.0.22: zenith-to-horizon gradient that matches a
    // real daytime sky (deeper cerulean overhead → soft haze at horizon).
    const skyGeo = new THREE.SphereGeometry(80, 32, 16);
    const skyMat = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      uniforms: {
        topColor: { value: new THREE.Color(0x4a90d9) },
        bottomColor: { value: new THREE.Color(0xc4dcef) },
      },
      vertexShader: `varying vec3 vWorldPos;
        void main() {
          vec4 worldPosition = modelMatrix * vec4(position, 1.0);
          vWorldPos = worldPosition.xyz;
          gl_Position = projectionMatrix * viewMatrix * worldPosition;
        }`,
      fragmentShader: `varying vec3 vWorldPos;
        uniform vec3 topColor;
        uniform vec3 bottomColor;
        void main() {
          float h = normalize(vWorldPos).y;
          gl_FragColor = vec4(mix(bottomColor, topColor, max(h,0.0)), 1.0);
        }`,
    });
    scene.add(new THREE.Mesh(skyGeo, skyMat));
    // Track the sky dome separately so Drafting Mode can hide it (the
    // shader dome occludes the scene.background swap to Blueprint Blue).
    const skyMesh = scene.children[scene.children.length - 1];
    sceneRef.current.userData._skyMesh = skyMesh;

    // Procedural textures
    sidingTexRef.current = makeSidingTexture(1);
    metalRoofTexRef.current = makeMetalRoofTexture();
    grassTexRef.current = makeGrassTexture();
    stairsTexRef.current = makeStairsTexture();
    brickTexRef.current = makeBrickTexture();
    stoneTexRef.current = makeStoneTexture();
    woodPlankTexRef.current = makeWoodPlankTexture();
    concreteTexRef.current = makeConcreteTexture();

    // Env group: ground / walkways / trees — exempt from section-cut clipping.
    const envGroup = new THREE.Group();
    scene.add(envGroup);
    envGroupRef.current = envGroup;

    const groundGeo = new THREE.PlaneGeometry(120, 120, 1, 1);
    const groundMat = new THREE.MeshStandardMaterial({
      map: grassTexRef.current,
      color: 0xffffff,
      roughness: 0.95,
      metalness: 0.0,
      clippingPlanes: [],
    });
    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    envGroup.add(ground);
    originalGroundRef.current = ground;
    groundRef.current = ground;

    const grid = new THREE.GridHelper(120, 120, 0x274058, 0x1a2942);
    grid.position.y = 0.005;
    grid.material.transparent = true;
    grid.material.opacity = 0.35;
    scene.add(grid);
    sceneRef.current.userData._grid = grid;

    // Lights
    const hemi = new THREE.HemisphereLight(0xbcd4ff, 0x223344, 0.55);
    scene.add(hemi);
    hemiRef.current = hemi;

    const sun = new THREE.DirectionalLight(0xfff4e0, 1.2);
    sun.position.set(22, 30, 14);
    sun.castShadow = true;
    sun.shadow.mapSize.width = 2048;
    sun.shadow.mapSize.height = 2048;
    sun.shadow.bias = -0.0006;
    sun.shadow.normalBias = 0.04;
    sun.shadow.radius = 6;
    sun.shadow.camera.left = -30;
    sun.shadow.camera.right = 30;
    sun.shadow.camera.top = 30;
    sun.shadow.camera.bottom = -30;
    sun.shadow.camera.near = 1;
    sun.shadow.camera.far = 80;
    scene.add(sun);
    scene.add(sun.target);
    sunRef.current = sun;

    // V1.6 — Drafting Mode ambient light. Idle at intensity 0 in realistic
    // mode (zero perf cost), bumped to 1.0 when drafting toggle activates.
    const ambient = new THREE.AmbientLight(0xffffff, 0);
    scene.add(ambient);
    ambientRef.current = ambient;

    const fill = new THREE.DirectionalLight(0x9bb6cc, 0.3);
    fill.position.set(-15, 12, -10);
    scene.add(fill);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.07;
    controls.minDistance = 0.5;
    controls.maxDistance = 60;
    controls.maxPolarAngle = Math.PI / 2 - 0.05;
    controls.target.set(0, 1.5, 0);
    controlsRef.current = controls;

    const houseGroup = new THREE.Group();
    // V2.0.17 — Mirror the world X axis at the group level so the 3D
    // scene's left/right matches the 2D Plan (east = right). The 2D plan
    // uses architectural convention (east on right, front at bottom). The
    // 3D camera sits south of the building (-Z), which naturally puts
    // world +X on the LEFT of screen. Mirroring the houseGroup along X
    // realigns the two views without altering the underlying blueprint
    // coordinates. The mini-map + teleport handlers below compensate by
    // negating x so user-facing data coords remain canonical.
    houseGroup.scale.x = -1;
    scene.add(houseGroup);
    houseGroupRef.current = houseGroup;
    // Mirror env children too so walkways / decks / trees attached at
    // world coords stay aligned with the mirrored building.
    const envGroupForMirror = envGroupRef.current;
    if (envGroupForMirror) envGroupForMirror.scale.x = -1;

    // Tap-to-teleport: registers pointer handlers and the cyan preview ring.
    teleport.attachToScene({
      scene, renderer, camera, controls, houseGroupRef, envGroupRef,
    });

    let frameCounter = 0;
    const tick = () => {
      animFrameRef.current = requestAnimationFrame(tick);
      const finished = teleport.runTeleportFrame();
      if (!teleport.teleportRef.current) controls.update();
      void finished;
      updateFromCamera(camera);
      // Smart Occlusion: when X-Ray Walls is ON, lerp interior wall opacity
      // based on camera distance — fade IN as you approach (within 5m), fade
      // OUT (back to opaque) as you leave. Cheap O(W) traversal per frame.
      if (xrayInteriorWallsRef.current && houseGroupRef.current) {
        applyInteriorOcclusion(houseGroupRef.current, camera);
      } else if (houseGroupRef.current) {
        // Restore opacity any time the toggle flips off, then no-op next frame.
        restoreInteriorOpacity(houseGroupRef.current);
      }
      // V1.5.2 — Seamless Navigation. Two effects layered over the exterior
      // shell: (a) Ghost Wall raycast (throttled to every 4 frames), and
      // (b) Dollhouse global fade based on camera-distance-from-center
      // (every other frame; uses a Box3 traversal so it's the more
      // expensive of the two). The commit step lerps both targets each frame.
      if (houseGroupRef.current) {
        if (frameCounter % 4 === 0) {
          applyGhostWall(houseGroupRef.current, camera, controls);
        }
        if (frameCounter % 2 === 0) {
          applyDollhouseFade(houseGroupRef.current, camera);
        }
        commitSeamlessNavOpacity(houseGroupRef.current, true);
      }
      frameCounter++;
      renderer.render(scene, camera);
    };
    tick();

    const onResize = () => {
      if (!mount) return;
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    const ro = new ResizeObserver(onResize);
    ro.observe(mount);

    return () => {
      cancelAnimationFrame(animFrameRef.current);
      ro.disconnect();
      teleport.detachFromScene();
      controls.dispose();
      renderer.dispose();
      sidingTexRef.current?.dispose();
      metalRoofTexRef.current?.dispose();
      grassTexRef.current?.dispose();
      stairsTexRef.current?.dispose();
      brickTexRef.current?.dispose();
      stoneTexRef.current?.dispose();
      woodPlankTexRef.current?.dispose();
      concreteTexRef.current?.dispose();
      if (renderer.domElement && mount.contains(renderer.domElement)) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, []);

  // ---- Re-render house from blueprint -------------------------------------
  // Heavy Three.js geometry construction is deferred a frame via
  // requestAnimationFrame so React can flush the latest UI state (e.g.,
  // chatLoading=false, the new message bubble) BEFORE the main-thread block.
  // This prevents the mobile chat from looking "locked" while a large house
  // rebuilds.
  useEffect(() => {
    const group = houseGroupRef.current;
    if (!group) return;
    const raf = requestAnimationFrame(() => {
      try {
        rebuildBlueprint();
        // V1.8.1 — auto-fit the camera in front of the actual house
        // bounding box (once on first build only).
        if (!firstFitDoneRef.current) {
          if (runAutoFit()) {
            firstFitDoneRef.current = true;
          }
        }
      } catch (err) {
        console.error("[ThreeViewport] geometry rebuild failed:", err);
        try {
          while (group.children.length > 0) {
            const c = group.children.pop();
            c.traverse?.(disposeNodeStatic);
            disposeNodeStatic(c);
          }
          const env = envGroupRef.current;
          if (env) {
            while (env.children.length > 0) {
              const c = env.children.pop();
              c.traverse?.(disposeNodeStatic);
              disposeNodeStatic(c);
            }
            groundRef.current = null;
            originalGroundRef.current = null;
          }
        } catch (cleanupErr) { console.debug("[ThreeViewport] cleanup after rebuild failure failed:", cleanupErr); }
        onRenderErrorRef.current?.(err);
      }
    });
    return () => cancelAnimationFrame(raf);

    function rebuildBlueprint() {    const disposeNode = (n) => {
      n.geometry?.dispose?.();
      if (Array.isArray(n.material)) n.material.forEach((m) => m.dispose?.());
      else n.material?.dispose?.();
    };
    while (group.children.length > 0) {
      const c = group.children.pop();
      c.traverse?.(disposeNode);
      disposeNode(c);
    }
    floorGroupsRef.current = {};

    const envGroup = envGroupRef.current;
    if (envGroup) {
      while (envGroup.children.length > 0) {
        const c = envGroup.children.pop();
        c.traverse?.(disposeNode);
        disposeNode(c);
      }
      groundRef.current = null;
      originalGroundRef.current = null;
    }

    if (!blueprint) {
      if (envGroup && grassTexRef.current) {
        const g = new THREE.Mesh(
          new THREE.PlaneGeometry(120, 120, 1, 1),
          new THREE.MeshStandardMaterial({
            map: grassTexRef.current, color: 0xffffff, roughness: 0.95,
            clippingPlanes: [],
          }),
        );
        g.rotation.x = -Math.PI / 2;
        g.receiveShadow = true;
        envGroup.add(g);
        groundRef.current = g;
        originalGroundRef.current = g;
      }
      return;
    }

    const sidingTex = sidingTexRef.current;
    const metalRoofTex = metalRoofTexRef.current;
    const stairsTex = stairsTexRef.current;

    let floors = Array.isArray(blueprint.floors) ? blueprint.floors : null;
    if (!floors || floors.length === 0) {
      floors = [{
        floor_index: 1, height_offset: 0, ceiling_height: 3,
        walls: blueprint.walls || [], windows: blueprint.windows || [],
        doors: blueprint.doors || [], rooms: blueprint.rooms || [],
        furniture: blueprint.furniture || [], floor_holes: blueprint.floor_holes || [],
      }];
    }
    floors = floors.map((f) => ({
      ...f,
      floor_index: Number(f.floor_index ?? f.level ?? 1),
      // height_offset_raw: undefined → we'll auto-compute below.
      _ho_raw: f.height_offset ?? f.height_offset_y,
      ceiling_height: Number(f.ceiling_height || 2.7),
    }));
    floors = [...floors].sort((a, b) => a.floor_index - b.floor_index);
    // V1.8.4 — Auto-stack: when the AI omits `height_offset` (common in Simple
    // Mode multi-floor designs), compute each floor's elevation as the cumul
    // of prior floors' `ceiling_height`. Ground floor (index 1) stays at y=0;
    // basements (index ≤ 0) get a negative offset; upstairs floors stack up.
    let runningY = 0;
    floors = floors.map((f) => {
      let ho;
      if (typeof f._ho_raw === "number" && !Number.isNaN(f._ho_raw)) {
        ho = Number(f._ho_raw);
      } else if (f.floor_index <= 0) {
        ho = -2.5; // basement default
      } else if (f.floor_index === 1) {
        ho = 0;
      } else {
        ho = runningY;
      }
      runningY = ho + f.ceiling_height;
      return { ...f, height_offset: ho };
    });

    const allLevels = floors.map((f) => f.floor_index);
    const selected = Array.isArray(visibleFloors) && visibleFloors.length > 0
      ? Number(visibleFloors[0]) : null;
    const opacityForLevel = (lv) => (selected != null && lv > selected ? 0.3 : 1.0);
    const applyOpacity = (g, opacity) => {
      g.traverse((node) => {
        if (!node.isMesh && !node.isLineSegments) return;
        const mats = Array.isArray(node.material) ? node.material : [node.material];
        mats.forEach((m) => {
          if (!m) return;
          if (opacity < 1.0) {
            m.transparent = true;
            m.opacity = m.userData?._origOpacity != null
              ? m.userData._origOpacity * opacity : opacity;
            m.depthWrite = false;
          }
        });
      });
    };

    const staircases = blueprint.staircases || [];
    const holesByFloor = {};
    staircases.forEach((s) => {
      const toIdx = Number(s.to_floor_index ?? s.to_floor ?? 0);
      if (!toIdx) return;
      const arr = holesByFloor[toIdx] || (holesByFloor[toIdx] = []);
      arr.push({
        id: `auto_hole_${s.id || `${s.from_floor_index || s.from_floor}_${toIdx}`}`,
        position: s.position || [0, 0],
        rotation_deg: s.rotation_deg || 0,
        width: Number(s.width || 1.0),
        length: Number(s.length || 4.0),
      });
    });

    let topBounds = null;
    let topEaveY = 0;
    let basementBounds = null;
    let basementY = 0;
    let selectedCutY = null;

    floors.forEach((floor) => {
      const fg = new THREE.Group();
      fg.position.y = Number(floor.height_offset || 0);
      const isBasement = Number(floor.height_offset || 0) < -0.1 || Number(floor.floor_index) <= 0;
      const { bounds } = buildFloor({
        floorGroup: fg, floor, blueprint,
        sidingTexture: sidingTex,
        metalRoofTexture: metalRoofTex,
        style,
        staircaseHoles: holesByFloor[floor.floor_index] || [],
        isBasement,
        concreteTexture: concreteTexRef.current,
      });
      // V1.9.3 — extrude any user-placed Symbol Palette items as low-poly
      // boxes (or stepped stairs) into the same floor group so they ride
      // the floor's height_offset + share its clipping plane.
      buildSymbolsForFloor(fg, floor);
      group.add(fg);
      const op = opacityForLevel(floor.floor_index);
      if (op < 1.0) applyOpacity(fg, op);
      floorGroupsRef.current[floor.floor_index] = fg;

      if (isBasement && bounds && isFinite(bounds.minX)) {
        basementBounds = bounds;
        basementY = Number(floor.height_offset || 0);
      }
      const wallH = Math.max(
        ...((floor.walls || []).map((w) => w.height || floor.ceiling_height || 3)),
        floor.ceiling_height || 3,
      );
      const eaveAbsY = Number(floor.height_offset || 0) + (isFinite(wallH) ? wallH : 3);
      if (eaveAbsY >= topEaveY && !isBasement) {
        topEaveY = eaveAbsY;
        topBounds = bounds;
      }
      if (selected != null && floor.floor_index === selected) {
        selectedCutY = eaveAbsY - 0.02;
      }
    });

    // Basement excavation pit (env group).
    if (basementBounds) {
      const margin = 0.6;
      const pMinX = basementBounds.minX - margin;
      const pMaxX = basementBounds.maxX + margin;
      const pMinZ = basementBounds.minZ - margin;
      const pMaxZ = basementBounds.maxZ + margin;

      if (groundRef.current) {
        envGroup.remove(groundRef.current);
        sceneRef.current.remove(groundRef.current);
        groundRef.current.geometry?.dispose?.();
        groundRef.current.material?.dispose?.();
        groundRef.current = null;
      }
      const groundShape = new THREE.Shape();
      const G = 60;
      groundShape.moveTo(-G, -G);
      groundShape.lineTo(G, -G);
      groundShape.lineTo(G, G);
      groundShape.lineTo(-G, G);
      groundShape.lineTo(-G, -G);
      const hole = new THREE.Path();
      hole.moveTo(pMinX, -pMinZ);
      hole.lineTo(pMaxX, -pMinZ);
      hole.lineTo(pMaxX, -pMaxZ);
      hole.lineTo(pMinX, -pMaxZ);
      hole.lineTo(pMinX, -pMinZ);
      groundShape.holes.push(hole);
      const newGround = new THREE.Mesh(
        new THREE.ShapeGeometry(groundShape),
        new THREE.MeshStandardMaterial({
          map: grassTexRef.current, color: 0xffffff, roughness: 0.95,
          metalness: 0.0, side: THREE.DoubleSide, clippingPlanes: [],
        }),
      );
      newGround.rotation.x = -Math.PI / 2;
      newGround.receiveShadow = true;
      envGroup.add(newGround);
      groundRef.current = newGround;
      if (originalGroundRef.current && originalGroundRef.current !== newGround) {
        originalGroundRef.current.visible = false;
      }

      const pitFloor = new THREE.Mesh(
        new THREE.PlaneGeometry(pMaxX - pMinX, pMaxZ - pMinZ),
        new THREE.MeshStandardMaterial({
          color: 0x3a2d1f, roughness: 1.0, metalness: 0.0, clippingPlanes: [],
        }),
      );
      pitFloor.rotation.x = -Math.PI / 2;
      pitFloor.position.set((pMinX + pMaxX) / 2, basementY - 0.02, (pMinZ + pMaxZ) / 2);
      pitFloor.receiveShadow = true;
      envGroup.add(pitFloor);

      const earthMat = new THREE.MeshStandardMaterial({
        color: 0x2e241a, roughness: 1.0, metalness: 0.0,
        side: THREE.DoubleSide, clippingPlanes: [],
      });
      const pitDepth = Math.abs(basementY);
      const pitW = pMaxX - pMinX;
      const pitL = pMaxZ - pMinZ;
      [
        { w: pitW, h: pitDepth, x: (pMinX + pMaxX) / 2, y: basementY / 2, z: pMinZ, rotY: 0 },
        { w: pitW, h: pitDepth, x: (pMinX + pMaxX) / 2, y: basementY / 2, z: pMaxZ, rotY: Math.PI },
        { w: pitL, h: pitDepth, x: pMinX, y: basementY / 2, z: (pMinZ + pMaxZ) / 2, rotY: -Math.PI / 2 },
        { w: pitL, h: pitDepth, x: pMaxX, y: basementY / 2, z: (pMinZ + pMaxZ) / 2, rotY: Math.PI / 2 },
      ].forEach((s) => {
        const mesh = new THREE.Mesh(new THREE.PlaneGeometry(s.w, s.h), earthMat);
        mesh.position.set(s.x, s.y, s.z);
        mesh.rotation.y = s.rotY;
        mesh.receiveShadow = true;
        envGroup.add(mesh);
      });
    } else if (!groundRef.current && envGroup && grassTexRef.current) {
      const g = new THREE.Mesh(
        new THREE.PlaneGeometry(120, 120, 1, 1),
        new THREE.MeshStandardMaterial({
          map: grassTexRef.current, color: 0xffffff, roughness: 0.95,
          clippingPlanes: [],
        }),
      );
      g.rotation.x = -Math.PI / 2;
      g.receiveShadow = true;
      envGroup.add(g);
      groundRef.current = g;
      originalGroundRef.current = g;
    }

    // Roof anchored to top floor
    const topFloor = floors.filter((f) => f.height_offset >= 0).slice(-1)[0]
      || floors[floors.length - 1];
    let roofMeta = null;
    if (topBounds && blueprint.roof) {
      const roofGroup = new THREE.Group();
      roofMeta = buildRoof({
        group: roofGroup, blueprint, bounds: topBounds, eaveY: topEaveY,
        materialFactory: (roof) =>
          buildRoofMaterial({ blueprint, roof, metalRoofTexture: metalRoofTex, style }),
      });
      const roofOp = opacityForLevel(topFloor.floor_index);
      if (roofOp < 1.0) applyOpacity(roofGroup, roofOp);
      group.add(roofGroup);
      floorGroupsRef.current[`roof_for_${topFloor.floor_index}`] = roofGroup;

      if (roofMeta) {
        roofMeta.sidingTexture = sidingTex;
        const fenestrationGroup = new THREE.Group();
        (blueprint.dormers || []).forEach((d) => {
          buildDormer({
            group: fenestrationGroup, dormer: d, roofMeta,
            materialFactory: (roof) =>
              buildRoofMaterial({ blueprint, roof, metalRoofTexture: metalRoofTex, style }),
          });
        });
        (blueprint.skylights || []).forEach((s) => {
          buildSkylight({ group: fenestrationGroup, skylight: s, roofMeta });
        });
        (blueprint.solar_panels || []).forEach((p) => {
          buildSolarPanel({ group: fenestrationGroup, panel: p, roofMeta });
        });
        if (roofOp < 1.0) applyOpacity(fenestrationGroup, roofOp);
        group.add(fenestrationGroup);
        floorGroupsRef.current[`fenestration_${topFloor.floor_index}`] = fenestrationGroup;
      }
    }

    // Staircases
    staircases.forEach((s) => {
      const fromIdx = Number(s.from_floor_index ?? s.from_floor);
      const toIdx = Number(s.to_floor_index ?? s.to_floor);
      const fromFloor = floors.find((f) => f.floor_index === fromIdx) || floors[0];
      const toFloor = floors.find((f) => f.floor_index === toIdx) || floors[floors.length - 1];
      const fromY = Number(fromFloor.height_offset || 0);
      const toY = Number(toFloor.height_offset || 0);
      if (toY <= fromY) return;
      const piece = buildStaircase({ stair: s, fromY, toY, stairsTexture: stairsTex });
      const op = opacityForLevel(toIdx);
      if (op < 1.0) applyOpacity(piece, op);
      group.add(piece);
      floorGroupsRef.current[`stair_${s.id || `${fromIdx}_${toIdx}`}`] = piece;
    });

    if (onFloorsAvailableRef.current) onFloorsAvailableRef.current(allLevels);

    // Site elements
    const textures = {
      brick: brickTexRef.current,
      stone: stoneTexRef.current,
      siding: sidingTexRef.current,
      concrete: concreteTexRef.current,
    };
    const chimneys = blueprint.chimneys || [];
    if (chimneys.length > 0 && roofMeta) {
      const ridgeY = roofMeta.eaveY + (roofMeta.span / 2) * Math.tan(roofMeta.pitchRad || 0);
      chimneys.forEach((ch) => {
        buildChimney({ group, chimney: ch, eaveY: roofMeta.eaveY, ridgeY, textures });
      });
    }
    (blueprint.decks || []).forEach((d) => {
      buildDeck({ group, deck: d, woodTexture: woodPlankTexRef.current });
    });
    (blueprint.walkways || []).forEach((w) => {
      buildWalkway({ group: envGroup, walkway: w, textures });
    });
    (blueprint.landscaping || []).forEach((t) => {
      buildTree({ group: envGroup, tree: t });
    });

    const wtFloor = (selected != null && floors.find((f) => f.floor_index === selected))
      || floors.find((f) => (f.height_offset || 0) >= 0)
      || floors[0];
    walkthroughFloorYRef.current = Number(wtFloor?.height_offset || 0) + 1.68;

    // Section-cut clipping (per-material on houseGroup; envGroup stays uncut).
    applyClipping(group, selectedCutY);
    // climateSunMultRef is consumed by the time-of-day effect — touched here
    // only to silence the lint exhaustive-deps warning when refactor needs it.
    void climateSunMultRef;
    // Apply current interior-wall visibility to the freshly-built scene.
    applyInteriorWallVisibility(group, showInteriorWalls);
    // V1.6 — re-apply Drafting Mode whenever the blueprint rebuilds. The
    // rebuild creates fresh meshes, so any prior material swap is lost. We
    // read the live ref so React doesn't need to retrigger this effect.
    if (sceneRef.current) {
      const state = draftingStateRef.current;
      state.renderer = rendererRef.current;
      state.sun = sunRef.current;
      state.hemi = hemiRef.current;
      state.ambient = ambientRef.current;
      state.envGroup = envGroupRef.current;
      applyDraftingMode(group, sceneRef.current, state, draftingModeRef.current);
    }
    } // end rebuildBlueprint
  }, [blueprint, style, visibleFloors, showInteriorWalls]);

  // ---- Interior wall visibility toggle ------------------------------------
  // Walls tagged in `buildFloor` carry userData.kind === "wall" and
  // userData.isExterior. When `showInteriorWalls === false`, hide every mesh
  // marked as interior (walls + their door leaves + window panes). Cheap pass.
  useEffect(() => {
    const group = houseGroupRef.current;
    if (!group) return;
    applyInteriorWallVisibility(group, showInteriorWalls);
  }, [showInteriorWalls, blueprint]);

  // ---- Sun positioning (Time of Day) --------------------------------------
  useEffect(() => {
    const sun = sunRef.current;
    if (!sun) return;
    const t = Math.max(0, Math.min(24, Number(timeOfDay) || 12));
    const dayProgress = (t - 5) / 14;
    const isDaytime = dayProgress >= 0 && dayProgress <= 1;
    const angle = Math.PI * Math.max(0, Math.min(1, dayProgress));
    const dist = 30;
    const x = Math.cos(angle) * dist;
    const y = Math.max(2, Math.sin(angle) * dist);
    const z = 8 + Math.cos(angle * 0.7) * 6;
    sun.position.set(-x, y, z);
    sun.target.position.set(0, 0, 0);
    sun.target.updateMatrixWorld();
    const noonness = isDaytime ? Math.sin(angle) : 0;
    const warm = isDaytime ? Math.max(0, 1 - Math.sin(angle)) : 0;
    sun.color.setRGB(
      1.0,
      Math.max(0.4, 0.9 + 0.1 * noonness - 0.15 * warm),
      Math.max(0.3, 0.7 + 0.3 * noonness - 0.3 * warm),
    );
    const climateMult = climateSunMultRef.current || 1.0;
    sun.intensity = (isDaytime ? 0.6 + 1.0 * noonness : 0.05) * climateMult;
  }, [timeOfDay, climateSunMultRef]);

  const sectionCutActive = Array.isArray(visibleFloors) && visibleFloors.length > 0;
  const compassNeedleRot = `rotate(${-compassDeg}deg)`;

  return (
    <div className="relative w-full h-full bg-zinc-950" data-testid="three-viewport">
      <div ref={mountRef} className="absolute inset-0" />
      <div className="pointer-events-none absolute top-4 left-4 z-10 font-mono text-[10px] uppercase tracking-widest text-brass-400/90">
        <div>// 3D Real-Time Render</div>
        <div className="text-zinc-500 mt-1">
          three.js • orbit • soft shadows • {sectionCutActive ? "section-cut active" : "x-ray ready"} • bim/live
        </div>
      </div>
      {sectionCutActive && (
        <div
          data-testid="section-cut-badge"
          className="pointer-events-none absolute top-14 right-4 z-10 font-mono text-[10px] uppercase tracking-widest text-orange-300 border border-orange-500/40 px-2 py-1 bg-zinc-950/70"
        >
          // section-cut: floor {Number(visibleFloors[0])}
        </div>
      )}

      <div
        data-testid="compass-widget"
        className="pointer-events-none absolute top-4 left-4 z-20 w-20 h-20 rounded-full border border-brass-500/40 bg-zinc-950/70 backdrop-blur-md grid place-items-center lg:left-[416px]"
      >
        <div
          data-testid="compass-needle"
          className="relative w-14 h-14"
          style={{ transform: compassNeedleRot, transition: "transform 60ms linear" }}
        >
          <span className="absolute -top-0.5 left-1/2 -translate-x-1/2 font-mono text-[8px] text-orange-300 font-bold tracking-wider">REAR</span>
          <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 font-mono text-[8px] text-brass-300 font-bold tracking-wider">FRONT</span>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-full w-[2px] h-4 bg-gradient-to-t from-transparent via-orange-400 to-orange-500 origin-bottom" />
        </div>
      </div>

      <div className="pointer-events-none absolute bottom-20 left-4 z-10 font-mono text-[10px] uppercase tracking-widest text-zinc-500 hidden md:block lg:left-[416px]">
        Tap floor to teleport · drag to rotate
      </div>
      <div className="pointer-events-none absolute bottom-4 left-4 z-10 font-mono text-[10px] uppercase tracking-widest text-zinc-500 lg:left-[416px]">
        Drag • Scroll • Right-click pan
      </div>

      {/* V1.7.9 — Mini-map overlay (top-down floor plan + camera frustum).
          V1.8.0 — Rooms are clickable: tap a room to teleport.
          V2.0.17 — Since houseGroup is mirrored along X (so 3D matches the
          2D Plan visually), negate the camera world-x → data-x before
          plotting, and negate the destination x before teleporting so the
          mini-map and 3D scene stay in lock-step. */}
      <MiniMap
        blueprint={blueprint}
        cameraPose={cameraPose ? {
          x: -cameraPose.x,
          z: cameraPose.z,
          yawDeg: -cameraPose.yawDeg,
        } : cameraPose}
        onRoomClick={(room) => {
          teleport.beginTeleport({
            x: -room.cx,
            z: room.cz,
            eyeY: 1.65,
            lookY: 1.2,
            camOffset: 4,
            durationMs: 1200,
          });
          onMiniMapTeleport?.(room);
        }}
      />
      {isLoading && (
        <div
          className="absolute inset-0 z-20 grid place-items-center bg-zinc-950/40 backdrop-blur-sm"
          data-testid="viewport-loading"
        >
          <div className="border border-brass-500/40 px-5 py-3 bg-zinc-950/80">
            <div className="font-mono text-[10px] uppercase tracking-widest text-brass-400 architecting-dot">
              Architecting structure...
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default ThreeViewport;
